import React from 'react';
import { Heart, MessageSquare, Trash2, CreditCard as Edit } from 'lucide-react';
import { ProfileDetailPage } from '../../pages/ProfileDetailPage';
import { useAuth } from '../../lib/auth-context';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, deleteDoc } from 'firebase/firestore';
import { db, storage } from '../../lib/firebase';
import { useNavigate } from 'react-router-dom';
import { TravelProfile } from '../../types/matching';
import { DeleteConfirmationModal } from '../DeleteConfirmationModal';
import { ref, deleteObject } from 'firebase/storage';
import { createNotification } from '../../lib/notification-service';
import { openWebRedirect } from '../../lib/api';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
}

export function ProfileModal({ isOpen, onClose, profileId }: ProfileModalProps) {
  const { user, userInfo, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = React.useState<TravelProfile | null>(null);
  const [isLiked, setIsLiked] = React.useState(false);
  const [isLiking, setIsLiking] = React.useState(false);
  const [isOwnProfile, setIsOwnProfile] = React.useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
  const [isDeletingProfile, setIsDeletingProfile] = React.useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Fetch profile data
  React.useEffect(() => {
    const fetchProfile = async () => {
      if (!profileId || !user) return;

      try {
        const profileRef = doc(db, 'travel_profiles', profileId);
        const profileDoc = await getDoc(profileRef);

        if (profileDoc.exists()) {
          const profileData = { id: profileDoc.id, ...profileDoc.data() } as TravelProfile;
          setProfile(profileData);
          setIsOwnProfile(profileData.userId === user.uid);

          // Check if user has liked this profile
          if (profileData.likes && Array.isArray(profileData.likes)) {
            setIsLiked(profileData.likes.includes(user.uid));
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    if (isOpen) {
      fetchProfile();
    }
  }, [profileId, user, isOpen]);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleToggleLike = async () => {
    if (!user || !profile) return;

    setIsLiking(true);
    try {
      const profileRef = doc(db, 'travel_profiles', profileId);

      if (isLiked) {
        await updateDoc(profileRef, {
          likes: arrayRemove(user.uid)
        });
        setIsLiked(false);
      } else {
        await updateDoc(profileRef, {
          likes: arrayUnion(user.uid)
        });
        setIsLiked(true);

        if (profile.userId && userInfo?.user?.username) {
          createNotification({
            recipientId: profile.userId,
            senderId: user.uid,
            senderUsername: userInfo.user.username,
            type: 'profile_like',
            messageId: profile.id,
            messagePreview: profile.firstName,
            threadCode: profile.id,
            threadType: 'profile',
            profileId: profile.id,
          });
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleMessage = () => {
    if (!user) {
      alert('Vous devez être connecté pour envoyer un message');
      return;
    }

    if (!profile?.userId) {
      alert('Ce profil n\'a pas d\'identifiant utilisateur');
      return;
    }

    const messageUrl = `https://pvtistes.net/forum/private.php?do=newpm&u=${profile.userId}&title=PVTmates`;
    openWebRedirect(messageUrl, userInfo);
  };

  const handleDeleteProfile = async () => {
    if (!profile || !user || (!isOwnProfile && !isAdmin)) return;

    setIsDeletingProfile(true);
    try {
      // Delete all photos from storage
      if (profile.photos && profile.photos.length > 0) {
        const deletePromises = profile.photos.map(async (photoUrl) => {
          try {
            const photoRef = ref(storage, photoUrl);
            await deleteObject(photoRef);
          } catch (error) {
            console.error('Error deleting photo:', error);
          }
        });
        await Promise.all(deletePromises);
      }

      // Delete the profile document
      await deleteDoc(doc(db, 'travel_profiles', profile.id));

      // Close modal and refresh
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error deleting profile:', error);
      alert('Une erreur est survenue lors de la suppression du profil');
    } finally {
      setIsDeletingProfile(false);
      setShowDeleteConfirmation(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Close Button - Fixed at top level */}
      <button
        onClick={onClose}
        className="fixed top-6 right-6 z-[100] p-2.5 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full transition-all duration-200 group shadow-lg"
        aria-label="Fermer"
      >
        <svg className="w-6 h-6 text-white group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div
        ref={modalRef}
        className="relative w-full max-w-[98vw] lg:max-w-[95vw] mx-auto h-full bg-gray-50 dark:bg-gray-900 overflow-y-auto animate-slide-up flex flex-col scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Profile Content */}
        <div className="flex-1">
          <ProfileDetailPage
            isModal={true}
            onModalClose={onClose}
            profileIdOverride={profileId}
            onDelete={(isOwnProfile || isAdmin) ? () => setShowDeleteConfirmation(true) : undefined}
            isDeletingProfile={isDeletingProfile}
          />
        </div>

        {profile && !isOwnProfile && (
          <div className="sticky bottom-0 left-0 right-0 z-[70] bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg border-t border-gray-100 dark:border-gray-700/50 p-3.5 sm:p-4 mt-auto">
            <div className="max-w-4xl mx-auto flex items-center gap-2.5">
              <button
                onClick={handleToggleLike}
                className={`flex items-center justify-center gap-2 px-5 py-3 rounded-xl transition-colors text-sm font-medium ${
                  isLiked
                    ? 'text-rose-600 bg-rose-50 hover:bg-rose-100 dark:text-rose-400 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/30'
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                }`}
                disabled={isLiking}
              >
                <Heart className={`w-4.5 h-4.5 ${isLiked ? 'fill-current' : ''}`} />
                <span className="hidden sm:inline">{isLiked ? "J'aime" : "J'aime"}</span>
              </button>
              <button
                onClick={handleMessage}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors text-sm font-medium shadow-sm"
              >
                <MessageSquare className="w-4.5 h-4.5" />
                <span>Envoyer un message</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteProfile}
        itemName={isOwnProfile ? "votre annonce PVTmates" : "cette annonce PVTmates"}
        confirmationWord="SUPPRIMER"
        description={isOwnProfile
          ? "Toutes vos photos et informations seront définitivement supprimées."
          : "L'annonce et toutes ses données seront définitivement supprimées."
        }
      />
    </div>
  );
}
